import React, { useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';

import type { Avatar, CallList, CallProgress, DataExportTemplate, Product, Profile } from 'bundles/connect/types';
import type { CallListUser } from './manage_call_list_users';
import { getCallListItems } from 'bundles/connect/helpers/prospect_api';
import configureStore from 'bundles/connect/components/store/configure';
import { loadCriteria, loadCallListTableViewFlag } from 'bundles/connect/helpers/local_storage';
import CallListTableView from './call_list_table_view';
import { DEFAULT_UI } from 'bundles/connect/components/store/reducers/ui';
import CallListMapView from './call_list_map_view';
import OffMarketPropertyOffcanvas from '../search/components/off_market_property_offcanvas';
import CallListItemsHeader from './call_list_items_header';
import { Card } from 'react-bootstrap-5';
import CallListItemsTableHeader from './call_list_items_table_header';
import { setLoading } from '../store/actions/ui';
import { setResults, setPage, staticCallListSearch } from '../store/actions/results';
import { UPDATE_CRITERIA } from '../store/actions/criteria';
import { setOffMarketPropertyIdPayload } from '../store/actions/off_market_property';

const NUM_PER_PAGE = 25;
const NUM_PINS_ON_MAP = 500;

export type CallListItemsProps = {
  avatars: Avatar[];
  callList: CallList;
  callListUsers: CallListUser[];
  profile: Profile;
  prospectTeamAccount: boolean;
  templates: DataExportTemplate[];
  exportDisabled: string;
  lastUpdatedMap: Map<number, Date>;
  callProgress: CallProgress;
  products: Product;
}

export default function CallListItems({
  avatars,
  callList,
  callListUsers,
  lastUpdatedMap,
  profile,
  prospectTeamAccount,
  templates,
  exportDisabled,
  callProgress,
  products
}: CallListItemsProps) {
  const [callListName, setCallListName] = useState(callList.name);
  const [tableView, setTableView] = useState(loadCallListTableViewFlag());
  const [pinsIndex, setPinsIndex] = useState(0);

  const criteria = loadCriteria()?.criteria || {};

  const store = useMemo(() => configureStore(
    {
      criteria: { mapBounds: criteria?.mapBounds },
      ui: {
        ...DEFAULT_UI,
        profile,
        callListTableView: loadCallListTableViewFlag(),
        showMap: true,
        products: products.products,
        offMarketSearch: true,
        searchTableColumnKeys: null,
        staticCallList: true
      },
      callConsoleData: {
        callListId: callList.id
      },
      results: {
        callListItems: [],
        total: 0,
        page: 1,
        loading: false
      }
    }
  ), []);

  useEffect(() => {
    let prevPage = store.getState().results.page || 1;

    const unsubscribe = store.subscribe(() => {
      const state = store.getState();
      const currentPage = state.results.page;
      setTableView(state.ui.callListTableView);

      if (currentPage !== prevPage) {
        prevPage = currentPage;
        store.dispatch(setLoading(true));

        getCallListItems({ page: currentPage }, callList.id).then(async (r) => {
          store.dispatch(setResults({
            callListItems: r.callListItems,
            total: r.total,
            page: currentPage,
            totalPages: Math.ceil(r.total / NUM_PER_PAGE)
          }));
          await store.dispatch(
            { type: UPDATE_CRITERIA, payload: { callListPropertyIds: r.callListItemIds } }
          );

          const newPinIndex = (NUM_PINS_ON_MAP * Math.floor((currentPage - 1) * NUM_PER_PAGE / NUM_PINS_ON_MAP));
          if (newPinIndex !== pinsIndex) {
            setPinsIndex(newPinIndex);
            store.dispatch<any>(staticCallListSearch());
          }
        }).finally(() => store.dispatch(setLoading(false)));
      }
    });

    // Initial fetch
    store.dispatch(setLoading(true));
    getCallListItems({ page: prevPage }, callList.id).then(async (r) => {
      store.dispatch(setResults({
        callListItems: r.callListItems,
        total: r.total,
        page: prevPage,
        totalPages: Math.ceil(r.total / NUM_PER_PAGE),
        totalOffMarketResults: r.callListItems.length
      }));
      await store.dispatch(
        { type: UPDATE_CRITERIA, payload: { callListPropertyIds: r.callListItemIds } }
      );
      store.dispatch<any>(staticCallListSearch());
    }).finally(() => store.dispatch(setLoading(false)));

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const handleItemRemoved = (e) => {
      const results = store.getState().results;

      if (e.detail.id) {
        store.dispatch(setResults({
          callListItems: results?.callListItems?.filter(item => item.id !== e.detail.id),
        }));

        if (results?.callListItems?.length === 1 && results.page > 1) {
          store.dispatch(setPage(prev => prev - 1));
        }
      }

      store.dispatch(setOffMarketPropertyIdPayload(''));
    };

    window.addEventListener('callListItemRemoved', handleItemRemoved);

    return () => {
      window.removeEventListener('callListItemRemoved', handleItemRemoved);
    };
  }, [store]);

  const COMMON_PROPS = {
    callList,
    lastUpdatedMap,
  };

  return (
    <Provider store={store}>
      <div className="position-absolute w-100 h-100 d-flex flex-column bg-white">
        <div className="d-flex flex-column flex-grow-1 overflow-hidden">
          <CallListItemsHeader
            avatars={avatars}
            callListId={callList.id}
            callListName={callListName}
            callProgress={callProgress}
            prospectTeamAccount={prospectTeamAccount}
          />
          <Card className={`border-start-0 border-end-0 rounded-0 ${ tableView ? 'd-flex flex-column h-100' : ''}`}>
            <Card.Header className="border-0">
              <CallListItemsTableHeader
                callList={callList}
                callListName={callListName}
                callListUsers={callListUsers}
                exportDisabled={exportDisabled}
                setCallListName={setCallListName}
                setPage={setPage}
                templates={templates}
              />
            </Card.Header>
            <CallListTableView {...COMMON_PROPS} />
          </Card>
          <CallListMapView {...COMMON_PROPS} nominatimUrl={profile.nominatimUrl} />
        </div>
      </div>
      <OffMarketPropertyOffcanvas fromConsole={false} />
    </Provider>
  );
}
